import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, RotateCw, RotateCcw, ZoomIn, ZoomOut, Check, RefreshCw } from 'lucide-react';

// تابع کمکی برای ساخت تصویر کراپ شده
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getRadianAngle = (degreeValue) => (degreeValue * Math.PI) / 180;

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // محاسبه ابعاد تصویر بعد از چرخش
  const rotRad = getRadianAngle(rotation);
  const sin = Math.abs(Math.sin(rotRad));
  const cos = Math.abs(Math.cos(rotRad));
  
  // ابعاد bounding box تصویر چرخیده
  const rotatedWidth = image.width * cos + image.height * sin;
  const rotatedHeight = image.width * sin + image.height * cos;

  // کانواس موقت برای رسم تصویر چرخیده
  const rotateCanvas = document.createElement('canvas');
  const rotateCtx = rotateCanvas.getContext('2d');
  
  rotateCanvas.width = rotatedWidth;
  rotateCanvas.height = rotatedHeight;

  // مرکز کانواس
  rotateCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
  rotateCtx.rotate(rotRad);
  rotateCtx.translate(-image.width / 2, -image.height / 2);
  rotateCtx.drawImage(image, 0, 0);

  // حالا از تصویر چرخیده، ناحیه crop رو ببر
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    rotateCanvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error('Canvas is empty');
        return;
      }
      const fileUrl = URL.createObjectURL(blob);
      resolve(fileUrl);
    }, 'image/jpeg', 0.95);
  });
}

// تابع برای rotate بدون crop
async function getRotatedImg(imageSrc, rotation) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const orientationChanged = rotation === 90 || rotation === -90 || rotation === 270 || rotation === -270;
  
  if (orientationChanged) {
    canvas.width = image.height;
    canvas.height = image.width;
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) return;
      const fileUrl = URL.createObjectURL(blob);
      resolve(fileUrl);
    }, 'image/jpeg', 0.95);
  });
}

function ImageCropper({ 
  imageSrc, 
  onClose, 
  onComplete, 
  lang = 'en',
  aspectRatio = 16 / 9 
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.5);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirm = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      onComplete(croppedImage);
    } catch (e) {
      console.error('Error cropping image:', e);
    }
  };

  const handleRotateLeft = () => {
    setRotation((prev) => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation((prev) => prev + 90);
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(0.5);
    setRotation(0);
  };

  const texts = {
    en: {
      title: 'Crop & Rotate Image',
      subtitle: 'Adjust your passport image before scanning',
      zoom: 'Zoom',
      rotation: 'Rotation',
      confirm: 'Confirm',
      cancel: 'Cancel',
      reset: 'Reset',
      rotateLeft: 'Rotate Left',
      rotateRight: 'Rotate Right',
    },
    fa: {
      title: 'برش و چرخش تصویر',
      subtitle: 'تصویر گذرنامه خود را قبل از اسکن تنظیم کنید',
      zoom: 'بزرگنمایی',
      rotation: 'چرخش',
      confirm: 'تأیید',
      cancel: 'انصراف',
      reset: 'بازنشانی',
      rotateLeft: 'چرخش به چپ',
      rotateRight: 'چرخش به راست',
    }
  };

  const t = texts[lang] || texts.en;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div 
        className="bg-[#1a1f2e] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto shadow-2xl border border-[#008a5e]/30"
        dir={lang === 'fa' ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#008a5e]/20">
          <div>
            <h2 className="text-lg font-bold text-white">{t.title}</h2>
            <p className="text-sm text-gray-400 mt-0.5">{t.subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-red-500/20 hover:bg-red-500/40 flex items-center justify-center text-red-400 hover:text-red-300 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Cropper Area */}
        <div className="relative h-[300px] sm:h-[350px] bg-black">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={0.1}
            maxZoom={3}
            rotation={rotation}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            cropShape="rect"
            showGrid={true}
            objectFit="contain"
            restrictPosition={false}
            style={{
              containerStyle: {
                background: '#000',
              },
              cropAreaStyle: {
                border: '2px solid #008a5e',
              }
            }}
          />
        </div>

        {/* Controls */}
        <div className="p-4 space-y-4 bg-[#141824]">
          {/* Quick Actions */}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={handleRotateLeft}
              className="flex items-center gap-2 px-4 py-2 bg-[#008a5e]/20 hover:bg-[#008a5e]/30 text-[#008a5e] rounded-lg transition-colors"
              title={t.rotateLeft}
            >
              <RotateCcw size={18} />
              <span className="text-sm hidden sm:inline">-90°</span>
            </button>
            
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600/20 hover:bg-gray-600/30 text-gray-400 rounded-lg transition-colors"
              title={t.reset}
            >
              <RefreshCw size={18} />
              <span className="text-sm hidden sm:inline">{t.reset}</span>
            </button>
            
            <button
              onClick={handleRotateRight}
              className="flex items-center gap-2 px-4 py-2 bg-[#008a5e]/20 hover:bg-[#008a5e]/30 text-[#008a5e] rounded-lg transition-colors"
              title={t.rotateRight}
            >
              <span className="text-sm hidden sm:inline">+90°</span>
              <RotateCw size={18} />
            </button>
          </div>

          {/* Zoom Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400 flex items-center gap-2">
                <ZoomIn size={16} />
                {t.zoom}
              </span>
              <span className="text-[#008a5e] font-mono">{Math.round(zoom * 100)}%</span>
            </div>
            <input
              type="range"
              min={0.1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#008a5e]"
              style={{
                background: `linear-gradient(to right, #008a5e 0%, #008a5e ${((zoom - 0.1) / 2.9) * 100}%, #374151 ${((zoom - 0.1) / 2.9) * 100}%, #374151 100%)`
              }}
            />
          </div>

          {/* Rotation Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400 flex items-center gap-2">
                <RotateCw size={16} />
                {t.rotation}
              </span>
              <span className="text-[#008a5e] font-mono">{rotation}°</span>
            </div>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={rotation}
              onChange={(e) => setRotation(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#008a5e]"
              style={{
                background: `linear-gradient(to right, #374151 0%, #374151 ${((rotation + 180) / 360) * 100}%, #008a5e ${((rotation + 180) / 360) * 100}%, #374151 ${((rotation + 180) / 360) * 100}%)`
              }}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 p-4 border-t border-[#008a5e]/20 bg-[#1a1f2e]">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 bg-gray-600/30 hover:bg-gray-600/50 text-gray-300 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
          >
            <X size={18} />
            {t.cancel}
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 py-3 px-4 bg-[#008a5e] hover:bg-[#006d4a] text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Check size={18} />
            {t.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImageCropper;